import { ApolloError } from '@apollo/client'
import { Cell, Grid, Text, VFlow } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { handleError } from 'components/error/globalErrorHandler'
import {
  DefaultFormFooter,
  Form,
  FormPrompt,
  FormRenderProps,
  simNaoEnumToBoolean,
  SimNaoRadioGroupField,
} from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { confirm } from 'components/modals/confirm'
import { useFlags } from 'config/useFlagsContext'
import {
  useCidadaoBuscaCpfCnsDnvNisLazyQuery,
  useHasCidadaoAgendamentoFuturoQuery,
  useSalvarCidadaoMutation,
} from 'graphql/hooks.generated'
import { FiltroDocumentosCidadaoInput } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import qs from 'qs'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { metaPath } from 'util/metaPath'
import { tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'

import { useSaudeIndigenaAuthorization } from '../authorization/useSaudeIndigenaAuthorization'
import { CardNovidadesCidadao } from '../components/CardNovidadesCidadao'
import { useNavigateToCidadaoCallbackUrl } from '../hooks/useNavigateToCidadaoCallbackUrl'
import { UrlParams } from './cadsus/components/ModalStatusSincronizacaoCadsus'
import { createCidadaoCalculator } from './calculator-cidadao'
import { AvisoDadosSensiveisCard } from './components/AvisoDadosSensiveisCard'
import { CompartilhamentoProntuarioFieldGroup } from './components/CompartilhamentoProntuarioFieldGroup'
import { CondicoesSaudeAutorreferidasFormSection } from './components/CondicoesSaudeAutorreferidasFormSection'
import { ContatosFormSection } from './components/ContatosFormSection'
import { DadosPessoaisFormSection } from './components/DadosPessoaisFormSection'
import { EnderecoFormSection } from './components/EnderecoFormSection'
import { InformacoesSociodemograficasFormSection } from './components/InformacoesSociodemograficasFormSection'
import { InformacoesSocioEconomicasFormSection } from './components/InformacoesSocioEconomicasFormSection'
import { ModalCidadaosDocumentoRepetidos } from './components/ModalCidadaosDocumentoRepetidos'
import { PeriodoAusenciaSaudeIndigenaFormSection } from './components/PeriodoAusenciaSaudeIndigenaFormSection'
import { RaniSaudeIndigenaFormSection } from './components/RaniSaudeIndigenaFormSection'
import { ResponsabilidadeAcompanhamentoFormSection } from './components/ResponsabilidadeAcompanhamentoFormSection'
import { SituacaoRuaFormSection } from './components/SituacaoRuaFormSection'
import { convertToCidadaoInput } from './converter-cidadao'
import { convertCidadaoBackendErrorsToForm } from './converter-cidadaoBackendErrors'
import { CidadaoTourIdEnum } from './tour-cidadao'
import { CidadaoFormModel } from './types/model-cidadao'
import { useCidadaoCreateTour } from './useCidadaoCreateTour'
import { cidadaoValidator } from './validator-cidadao'

interface CidadaoFormProps {
  initialValues?: CidadaoFormModel
  needLoginGovBr: boolean
  isCadsusHabilitado: boolean
  isUsuarioAps: boolean
  isCadastroOrigemForaAps?: boolean
  isCadsusButtonVisible?: boolean
  sincronizarCadsus(cidadaoId: string, cns: string): void
}

const meta = metaPath<CidadaoFormModel>()

export function CidadaoForm(props: Readonly<CidadaoFormProps>) {
  const {
    initialValues,
    isCadsusHabilitado,
    needLoginGovBr,
    sincronizarCadsus,
    isUsuarioAps,
    isCadastroOrigemForaAps = false,
    isCadsusButtonVisible = false,
  } = props

  const [save, { loading }] = useSalvarCidadaoMutation()
  const handleRejection = useErrorHandler()
  const { PILOTO_ATENDIMENTO_SOCIOEDUCATIVO_ENABLED } = useFlags()

  const decorators = useMemo(() => [createCidadaoCalculator(meta)], [])
  const { getServerTimeNow } = useServerTime()
  const serverTime = getServerTimeNow()
  const history = useHistory()
  const { data: sessionData } = useSession({ fetchPolicy: 'cache-only' })

  const navigateToCidadaoCallbackUrl = useNavigateToCidadaoCallbackUrl()
  const location = useLocation()

  const { hasCadastrarEditarAldeadoPermission } = useSaudeIndigenaAuthorization()

  const [openModalDuplicidadeCidadao, setOpenModalDuplicidadeCidadao] = useState(false)
  const [blockFormPrompt, setBlockFormPrompt] = useState(false)

  const urlParams: UrlParams = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })

  const transformResult = async (
    result: Promise<void>,
    suppressNotificationError = false,
    suppressValidationError = false,
    supressFieldValidationNotificationError = false
  ) => {
    try {
      return await result
    } catch (error) {
      const handled = handleError({
        error,
        suppressNotificationError,
        suppressValidationError,
        supressFieldValidationNotificationError,
      })
      return convertCidadaoBackendErrorsToForm(handled)
    }
  }

  const handleClickCancelar = (cidadaoId: string) => {
    sessionStorage.removeItem('cidadaoCadsus')
    navigateToCidadaoCallbackUrl(cidadaoId)
  }

  const handleClickContinuar = (cidadaoId: string) => {
    sessionStorage.removeItem('cidadaoCadsus')
    if (urlParams?.callbackUrl) {
      navigateToCidadaoCallbackUrl(cidadaoId)
    } else {
      history.push(`/cidadao/${cidadaoId}/informacoes`)
    }
  }

  const getIsModoSimplificadoForaAps = (values: CidadaoFormModel) =>
    !values?.dadosPessoais?.isCidadaoAldeado && !isUsuarioAps && (isNovoCadastro || isCadastroOrigemForaAps)

  const handleSubmit = async (values: CidadaoFormModel) => {
    try {
      const result = await save({
        variables: {
          input: convertToCidadaoInput(values, {
            omitVinculacaoTerritorio: getIsModoSimplificadoForaAps(values),
          }),
        },
      })

      if (!result?.data?.salvarCidadao) {
        return result?.errors?.length ? result : undefined
      }

      const { id, cns } = result.data.salvarCidadao

      if (isCadsusHabilitado && !needLoginGovBr) {
        sincronizarCadsus(id, cns)
      } else {
        handleClickContinuar(id)
      }
    } catch (err) {
      handleRejection(err as ApolloError)
      throw err
    }
  }

  const [executeBuscaCidadaosDuplicados, { data: cidadaosDuplicados }] = useCidadaoBuscaCpfCnsDnvNisLazyQuery()

  const { refetch: refetchAgendamentos } = useHasCidadaoAgendamentoFuturoQuery({ skip: true })

  const handlePesquisarCidadaos = (filtro: FiltroDocumentosCidadaoInput) => {
    executeBuscaCidadaosDuplicados({
      variables: {
        filtro,
      },
      fetchPolicy: 'cache-first',
    })
  }

  const isNovoCadastro = !initialValues?.id
  const usuarioId = sessionData?.profissional?.usuario?.id

  const { isCardLembreteAtivo, isLembretesLoading, startCardTour } = useCidadaoCreateTour({
    usuarioId,
    isUsuarioAps,
    isNovoCadastro,
    isCadastroOrigemForaAps,
    isCadsusButtonVisible,
  })

  useEffect(() => {
    if (isNovoCadastro && !!cidadaosDuplicados?.cidadaosByDocumentos?.length) {
      setOpenModalDuplicidadeCidadao(true)
    }
  }, [cidadaosDuplicados?.cidadaosByDocumentos, isNovoCadastro])

  const renderForm = (formProps: FormRenderProps<CidadaoFormModel>) => {
    const { values, errors, form } = formProps
    const cidadaoFaleceu = values?.dadosPessoais?.faleceu
    const isCidadaoAldeado = values?.dadosPessoais?.isCidadaoAldeado
    const isEnderecoIndigena = tipoEnderecoIndigena.includes(values.tipoEndereco)
    const responsavelFamiliar = values?.responsabilidadeAcompanhamento?.responsavelFamiliar
    const isModoSimplificadoForaAps = getIsModoSimplificadoForaAps(values)

    const confirmDialog = (
      semVinculo: boolean,
      semDoc: boolean,
      perdeVinculo: boolean,
      hasAgendamentoFuturo: boolean,
      hasCnsAlterado: boolean,
      hasCpfAlterado: boolean
    ) => {
      confirm({
        title: 'Deseja salvar o cadastro individual?',
        icon: 'questionMarkOutline',
        confirmLabel: 'Continuar',
        cancelLabel: 'Cancelar',
        type: 'primary',
        size: 'large',
        onConfirm: formProps.handleSubmit,
        render: () => (
          <VFlow vSpacing={2}>
            {responsavelFamiliar && (
              <>
                {!responsavelFamiliar.id && (
                  <Text>
                    Não será possível vincular a família pois o CPF/CNS do responsável familiar informado não está
                    cadastrado na base local.
                  </Text>
                )}

                {!responsavelFamiliar.uuidUltimaFicha && responsavelFamiliar.id && (
                  <Text>
                    Não será possível vincular a família pois o responsável familiar informado ainda não possui Cadastro
                    Individual.
                  </Text>
                )}

                {!responsavelFamiliar.statusResponsavelFamiliar &&
                  responsavelFamiliar.uuidUltimaFicha &&
                  responsavelFamiliar.id && (
                    <Text>
                      Não será possível vincular a família pois o responsável familiar informado não está definido como
                      responsável em seu próprio Cadastro Individual.
                    </Text>
                  )}
              </>
            )}
            {semVinculo && (
              <Text>
                O cidadão não está vinculado a nenhuma equipe. O cadastro permanecerá sem vínculo com uma equipe
                responsável.
              </Text>
            )}
            {semDoc && (
              <Text>
                Cidadão sem CPF ou CNS registrado. Este cadastro não será considerado elegível para contabilização nos
                indicadores de saúde do município.
              </Text>
            )}
            {perdeVinculo && (
              <Text>Ao informar óbito do cidadão, ele perderá o vínculo com a sua equipe responsável.</Text>
            )}
            {hasAgendamentoFuturo && (
              <Text>Ao informar óbito do cidadão, os agendamentos futuros serão cancelados.</Text>
            )}
            {(hasCnsAlterado || hasCpfAlterado) && (
              <Text>
                ATENÇÂO: O {hasCnsAlterado && !hasCpfAlterado ? 'CNS' : 'CPF'} desse cadastro está sendo alterado. Isso
                não alterará os registros clínicos de sistemas terceiros, dos aplicativos e-SUS APS ou do CDS já salvos
                com o alterado {hasCnsAlterado && !hasCpfAlterado ? 'CNS' : 'CPF'} antigo neste prontuário, que podem
                deixar de ser apresentados no histórico do cidadão. Avalie com cuidado a necessidade dessa alteração
                antes de prosseguir.
              </Text>
            )}
          </VFlow>
        ),
      })()
    }

    const handleRollbackCpfCns = (event: React.MouseEvent) => {
      event.preventDefault()

      form.change(meta.dadosPessoais.stNaoPossuiCpf.absolutePath(), false)
      form.change(meta.dadosPessoais.cpf.absolutePath(), initialValues.dadosPessoais?.cpf)
      form.change(meta.dadosPessoais.cns.absolutePath(), initialValues.dadosPessoais?.cns)
    }

    const handleClickSalvar = async () => {
      let hasAgendamentoFuturo = false

      if (values.id && cidadaoFaleceu && values.dadosPessoais?.dataObito) {
        const { data } = await refetchAgendamentos({
          input: { id: values.id, dataReferencia: values.dadosPessoais.dataObito },
        })
        hasAgendamentoFuturo = data?.hasAgendamentoFuturo ?? false
      }

      const temErroResponsavel =
        Object.keys(errors).length === 0 &&
        responsavelFamiliar &&
        (!responsavelFamiliar?.id ||
          !responsavelFamiliar?.statusResponsavelFamiliar ||
          !responsavelFamiliar.uuidUltimaFicha)

      const semVinculo = !values.responsabilidadeAcompanhamento?.responsavel?.lotacao && !cidadaoFaleceu
      const semDoc = !values.dadosPessoais?.cpf && !values.dadosPessoais?.cns
      const perdeVinculo =
        !values.responsabilidadeAcompanhamento?.responsavel?.lotacao &&
        cidadaoFaleceu &&
        initialValues?.responsabilidadeAcompanhamento?.responsavel?.lotacao &&
        !initialValues?.dadosPessoais?.faleceu

      const hasCnsAlterado =
        initialValues?.dadosPessoais?.cns && initialValues.dadosPessoais.cns !== values.dadosPessoais?.cns
      const hasCpfAlterado =
        initialValues?.dadosPessoais?.cpf && initialValues.dadosPessoais.cpf !== values.dadosPessoais?.cpf

      if (
        temErroResponsavel ||
        semVinculo ||
        semDoc ||
        perdeVinculo ||
        hasAgendamentoFuturo ||
        hasCnsAlterado ||
        hasCpfAlterado
      ) {
        confirmDialog(semVinculo, semDoc, perdeVinculo, hasAgendamentoFuturo, hasCnsAlterado, hasCpfAlterado)
      } else {
        formProps.handleSubmit()
      }
    }

    return (
      <Grid>
        <FormPrompt ignorePromptWhenSubmitting disabled={blockFormPrompt} />
        {isLembretesLoading && (
          <Cell size={12}>
            <LoadingIndicator />
          </Cell>
        )}
        {!isLembretesLoading && isCardLembreteAtivo && (
          <Cell size={12}>
            <CardNovidadesCidadao onClickAcaoPrincipal={startCardTour} />
          </Cell>
        )}
        <Cell size={12}>
          <DadosPessoaisFormSection
            meta={meta.dadosPessoais}
            metaIndigena={meta.cidadaoAldeado}
            values={values.dadosPessoais}
            valuesIndigena={values.cidadaoAldeado}
            serverTime={serverTime}
            isCidadaoAldeado={isCidadaoAldeado}
            hasCadastrarEditarAldeadoPermission={hasCadastrarEditarAldeadoPermission}
            errors={errors.dadosPessoais}
            isNovoCadastro={isNovoCadastro}
            onPesquisarCidadaos={handlePesquisarCidadaos}
            onRollbackCpfCns={handleRollbackCpfCns}
          />
        </Cell>
        <Cell size={12}>
          <ContatosFormSection
            meta={meta.contatos}
            nacionalidade={values.dadosPessoais?.nacionalidadeCidadao?.nacionalidade}
            isCidadaoAldeado={isCidadaoAldeado}
          />
        </Cell>
        {!isModoSimplificadoForaAps && (
          <Cell size={12}>
            <ResponsabilidadeAcompanhamentoFormSection
              meta={meta.responsabilidadeAcompanhamento}
              values={formProps.values.responsabilidadeAcompanhamento}
              cidadaoFaleceu={cidadaoFaleceu}
              isCidadaoAldeado={isCidadaoAldeado}
              cpfCidadao={values.dadosPessoais.cpf}
              cnsCidadao={values.dadosPessoais.cns}
              isModoSimplificadoForaAps={isModoSimplificadoForaAps}
              isCadIndividual
              apenasAps={isUsuarioAps}
            />
          </Cell>
        )}
        <Cell size={12}>
          <EnderecoFormSection
            meta={meta}
            formProps={formProps}
            values={values}
            isCidadaoAldeado={isCidadaoAldeado}
            isEnderecoIndigena={isEnderecoIndigena}
          />
        </Cell>
        <Cell size={12}>
          <SimNaoRadioGroupField name={meta.statusSituacaoRua} label='Cidadão em situação de rua?' required />
        </Cell>
        {!isModoSimplificadoForaAps && simNaoEnumToBoolean[values?.statusSituacaoRua] && (
          <Cell size={12}>
            <SituacaoRuaFormSection meta={meta.situacaoRua} values={values?.situacaoRua} />
          </Cell>
        )}
        {isCidadaoAldeado && (
          <Fragment>
            <Cell size={5}>
              <PeriodoAusenciaSaudeIndigenaFormSection
                meta={meta.cidadaoAldeado}
                values={values.cidadaoAldeado}
                dataNascimento={values?.dadosPessoais?.dataNascimento}
              />
            </Cell>
            <Cell size={7} />
            <Cell size={12}>
              <RaniSaudeIndigenaFormSection
                meta={meta.cidadaoAldeado}
                dataNascimento={values?.dadosPessoais?.dataNascimento}
              />
            </Cell>
          </Fragment>
        )}
        {!isModoSimplificadoForaAps && (
          <>
            <Cell size={12}>
              <AvisoDadosSensiveisCard />
            </Cell>
            <Cell size={12}>
              <InformacoesSociodemograficasFormSection
                meta={meta.informacoesSociodemograficas}
                values={values.informacoesSociodemograficas}
                dataNascimento={values?.dadosPessoais?.dataNascimento}
                isAtendimentoSocioeducativoEnabled={PILOTO_ATENDIMENTO_SOCIOEDUCATIVO_ENABLED}
              />
            </Cell>
            <Cell size={12}>
              <InformacoesSocioEconomicasFormSection
                meta={meta.informacoesSocioEconomicas}
                values={values.informacoesSocioEconomicas}
                metaCidadaoAldeado={meta.cidadaoAldeado}
                cidadaoAldeado={values.cidadaoAldeado}
                isCidadaoAldeado={isCidadaoAldeado}
              />
            </Cell>
            <Cell size={12}>
              <CondicoesSaudeAutorreferidasFormSection
                meta={meta.condicoesSaudeAutorreferidas}
                values={values?.condicoesSaudeAutorreferidas}
                sexo={values?.dadosPessoais?.sexo}
                dataNascimento={values?.dadosPessoais?.dataNascimento}
              />
            </Cell>
          </>
        )}
        <Cell size={12}>
          <CompartilhamentoProntuarioFieldGroup renderHeading name={meta} />
        </Cell>
        <Cell size={12}>
          <DefaultFormFooter
            buttonsId={CidadaoTourIdEnum.FORM_CONFIRMAR}
            handleSubmit={handleClickSalvar}
            onCancel={() => handleClickCancelar(values?.id)}
            loading={loading}
          />
        </Cell>
        <ModalCidadaosDocumentoRepetidos
          cidadaos={cidadaosDuplicados?.cidadaosByDocumentos}
          isModalOpen={openModalDuplicidadeCidadao}
          onClose={() => setOpenModalDuplicidadeCidadao(false)}
          onSubmitAtualizarCidadaoDuplicado={() => setBlockFormPrompt(true)}
        />
      </Grid>
    )
  }

  return (
    <Form<CidadaoFormModel>
      initialValues={initialValues}
      decorators={decorators}
      onSubmit={handleSubmit}
      render={renderForm}
      validate={cidadaoValidator(cidadaosDuplicados?.cidadaosByDocumentos, {
        isUsuarioAps,
        isCadastroOrigemForaAps,
      })}
      transformResult={transformResult}
    />
  )
}
